import { AlertCircle, Download, Copy, Check } from 'lucide-react';
import type { QueryExecutionResult } from '../../types/query';
import { useState } from 'react';

interface QueryResultViewerProps {
  result: QueryExecutionResult;
  showExport?: boolean;
}

export default function QueryResultViewer({ result, showExport = true }: QueryResultViewerProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyJson = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(JSON.stringify(result.data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleExportCsv = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (result.data.length === 0) return;

    const headers = result.columns.join(',');
    const rows = result.data.map(row =>
      result.columns.map(col => {
        const value = row[col];
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `query-result-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  console.log(result)
  if (!result.success) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-800">Erro na execução</h4>
            <p className="text-sm text-red-700 mt-1 font-mono">{result.error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (result.data.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-500">Nenhum resultado encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showExport && (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleCopyJson}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
              copied
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copiado!' : 'Copiar JSON'}
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-80">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {result.columns.map((column) => (
                  <th
                    key={column}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {result.data.slice(0, 100).map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50">
                  {result.columns.map((column) => (
                    <td
                      key={column}
                      className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap max-w-xs truncate"
                      title={String(row[column] ?? '')}
                    >
                      {formatCellValue(row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {result.data.length > 100 && (
          <div className="bg-gray-50 px-4 py-2 text-sm text-gray-500 text-center border-t border-gray-200">
            Mostrando 100 de {result.data.length} resultados
          </div>
        )}
      </div>
    </div>
  );
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
